import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Package, Minus, Plus, ArrowRightLeft, Loader2, AlertCircle, 
  Warehouse, Truck, MapPin, User
} from 'lucide-react';
import { warehouseAPI, vansAPI, scanAPI, itemsAPI, settingsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export const ScanActionSheet = ({ 
  open, 
  onClose, 
  scanResult, 
  context = 'warehouse',  // 'warehouse' | 'vehicle'
  vehicleId = null,
  onActionComplete
}) => {
  const { isOwner } = useAuth();
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(null);  // 'use' | 'restock' | 'transfer'
  const [quantity, setQuantity] = useState('1');
  const [note, setNote] = useState('');
  const [targetVehicleId, setTargetVehicleId] = useState('');
  const [targetLocation, setTargetLocation] = useState('warehouse');  // 'warehouse' | 'vehicle'
  const [vehicles, setVehicles] = useState([]);
  const [allowWarehouseUse, setAllowWarehouseUse] = useState(false);

  useEffect(() => {
    if (open) {
      // Load vehicles for transfer (only active, non-archived)
      const loadVehicles = async () => {
        try {
          const res = await vansAPI.list();
          setVehicles(res.data.filter(v => v.is_active && !v.archived_at));
        } catch (err) {
          console.error('Failed to load vehicles:', err);
        }
      };
      
      // Load settings
      const loadSettings = async () => {
        try {
          const res = await settingsAPI.get();
          setAllowWarehouseUse(res.data.allow_warehouse_use);
        } catch (err) {
          console.error('Failed to load settings:', err);
        }
      };
      
      loadVehicles();
      loadSettings();
      setAction(null);
      setQuantity('1');
      setNote('');
      setTargetVehicleId('');
      setTargetLocation('warehouse');
    }
  }, [open]);

  if (!open || !scanResult) return null;

  const { item_name, sku, category_names, stock_locations, item_id } = scanResult;
  
  // Find current location stock
  const warehouseStock = stock_locations.find(l => l.location_type === 'warehouse');
  const currentVehicleStock = vehicleId 
    ? stock_locations.find(l => l.location_type === 'vehicle' && l.location_id === vehicleId)
    : null;

  const handleAction = async () => {
    if (!action || !quantity || parseInt(quantity) < 1) {
      toast.error('Please select an action and enter a valid quantity');
      return;
    }

    setLoading(true);
    try {
      const qty = parseInt(quantity);
      
      if (context === 'warehouse') {
        if (action === 'use') {
          await warehouseAPI.use({ item_id, quantity: qty, note: note || undefined });
          toast.success(`Used ${qty} ${item_name} from warehouse`);
        } else if (action === 'restock') {
          await warehouseAPI.restock({ item_id, quantity: qty, note: note || undefined });
          toast.success(`Added ${qty} ${item_name} to warehouse`);
        } else if (action === 'transfer') {
          if (!targetVehicleId) {
            toast.error('Please select a destination vehicle');
            setLoading(false);
            return;
          }
          await warehouseAPI.transfer({
            from_location_type: 'warehouse',
            from_location_id: warehouseStock?.location_id,
            to_location_type: 'vehicle',
            to_location_id: targetVehicleId,
            item_id,
            quantity: qty,
            note: note || undefined
          });
          const targetVehicle = vehicles.find(v => v.id === targetVehicleId);
          toast.success(`Transferred ${qty} ${item_name} to ${targetVehicle?.name}`);
        }
      } else if (context === 'vehicle' && vehicleId) {
        if (action === 'use') {
          await vansAPI.useItem(vehicleId, { item_id, quantity: qty, note: note || undefined });
          toast.success(`Used ${qty} ${item_name}`);
        } else if (action === 'restock') {
          await vansAPI.restockItem(vehicleId, { item_id, quantity: qty, note: note || undefined });
          toast.success(`Added ${qty} ${item_name}`);
        } else if (action === 'transfer') {
          if (targetLocation === 'warehouse') {
            await warehouseAPI.transfer({
              from_location_type: 'vehicle',
              from_location_id: vehicleId,
              to_location_type: 'warehouse',
              to_location_id: warehouseStock?.location_id,
              item_id,
              quantity: qty,
              note: note || undefined
            });
            toast.success(`Transferred ${qty} ${item_name} to warehouse`);
          } else if (targetVehicleId) {
            await vansAPI.transfer({
              from_van_id: vehicleId,
              to_van_id: targetVehicleId,
              item_id,
              quantity: qty,
              note: note || undefined
            });
            const targetVehicle = vehicles.find(v => v.id === targetVehicleId);
            toast.success(`Transferred ${qty} ${item_name} to ${targetVehicle?.name}`);
          }
        }
      }

      onActionComplete?.();
      onClose();
    } catch (err) {
      const message = err.response?.data?.detail || 'Action failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            {item_name}
          </DialogTitle>
          {sku && (
            <DialogDescription className="flex items-center gap-2">
              SKU: {sku}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {/* Categories */}
          {category_names?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {category_names.map((name, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">
                  {name}
                </Badge>
              ))}
            </div>
          )}

          {/* Stock Info */}
          <div className="bg-muted/50 rounded-lg p-3 space-y-2">
            <h4 className="font-medium text-sm">Stock Levels</h4>
            {stock_locations.length === 0 ? (
              <p className="text-sm text-muted-foreground">No stock on hand</p>
            ) : (
              <div className="space-y-1">
                {stock_locations.map((loc, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      {loc.location_type === 'warehouse' ? (
                        <Warehouse className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <Truck className="w-4 h-4 text-muted-foreground" />
                      )}
                      <span>{loc.location_name}</span>
                      {loc.location_label && (
                        <span className="text-xs text-muted-foreground">
                          <MapPin className="w-3 h-3 inline mr-1" />
                          {loc.location_label}
                        </span>
                      )}
                      {loc.assigned_tech_name && (
                        <span className="text-xs text-muted-foreground">
                          <User className="w-3 h-3 inline mr-1" />
                          {loc.assigned_tech_name}
                        </span>
                      )}
                    </div>
                    <span className={`font-medium ${loc.quantity < 0 ? 'text-destructive' : ''}`}>
                      {loc.quantity}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Selection */}
          {!action ? (
            <div className="space-y-2">
              <Label>Select Action</Label>
              <div className="grid grid-cols-1 gap-2">
                {context === 'warehouse' ? (
                  <>
                    <Button 
                      variant="outline" 
                      className="justify-start h-auto py-3"
                      onClick={() => setAction('transfer')}
                    >
                      <ArrowRightLeft className="w-5 h-5 mr-3 text-primary" />
                      <div className="text-left">
                        <div className="font-medium">Transfer to Vehicle</div>
                        <div className="text-xs text-muted-foreground">Move stock to a vehicle</div>
                      </div>
                    </Button>
                    {isOwner && (
                      <Button 
                        variant="outline" 
                        className="justify-start h-auto py-3"
                        onClick={() => setAction('restock')}
                      >
                        <Plus className="w-5 h-5 mr-3 text-green-600" />
                        <div className="text-left">
                          <div className="font-medium">Add Stock</div>
                          <div className="text-xs text-muted-foreground">Receive inventory into warehouse</div>
                        </div>
                      </Button>
                    )}
                    {isOwner && allowWarehouseUse && (
                      <Button 
                        variant="outline" 
                        className="justify-start h-auto py-3"
                        onClick={() => setAction('use')}
                      >
                        <Minus className="w-5 h-5 mr-3 text-amber-600" />
                        <div className="text-left">
                          <div className="font-medium">Use / Consume</div>
                          <div className="text-xs text-muted-foreground">Remove without transfer</div>
                        </div>
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <Button 
                      variant="outline" 
                      className="justify-start h-auto py-3"
                      onClick={() => setAction('use')}
                    >
                      <Minus className="w-5 h-5 mr-3 text-amber-600" />
                      <div className="text-left">
                        <div className="font-medium">Use / Consume</div>
                        <div className="text-xs text-muted-foreground">Used on a job</div>
                      </div>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="justify-start h-auto py-3"
                      onClick={() => setAction('restock')}
                    >
                      <Plus className="w-5 h-5 mr-3 text-green-600" />
                      <div className="text-left">
                        <div className="font-medium">Add Stock</div>
                        <div className="text-xs text-muted-foreground">Manual correction</div>
                      </div>
                    </Button>
                    <Button 
                      variant="outline" 
                      className="justify-start h-auto py-3"
                      onClick={() => setAction('transfer')}
                    >
                      <ArrowRightLeft className="w-5 h-5 mr-3 text-primary" />
                      <div className="text-left">
                        <div className="font-medium">Transfer</div>
                        <div className="text-xs text-muted-foreground">To warehouse or another vehicle</div>
                      </div>
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Action Form */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">
                    {action === 'use' && 'Use / Consume'}
                    {action === 'restock' && 'Add Stock'}
                    {action === 'transfer' && 'Transfer'}
                  </Label>
                  <Button variant="ghost" size="sm" onClick={() => setAction(null)}>
                    Change
                  </Button>
                </div>

                {/* Transfer destination */}
                {action === 'transfer' && context === 'vehicle' && (
                  <div className="space-y-2">
                    <Label>Transfer To</Label>
                    <Select value={targetLocation} onValueChange={setTargetLocation}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="warehouse">Warehouse</SelectItem>
                        <SelectItem value="vehicle">Another Vehicle</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {action === 'transfer' && (context === 'warehouse' || targetLocation === 'vehicle') && (
                  <div className="space-y-2">
                    <Label>Destination Vehicle</Label>
                    <Select value={targetVehicleId} onValueChange={setTargetVehicleId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select vehicle" />
                      </SelectTrigger>
                      <SelectContent>
                        {vehicles.filter(v => v.id !== vehicleId).map(v => (
                          <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Note (optional)</Label>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add a note..."
                    rows={2}
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1" 
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1" 
                  onClick={handleAction}
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Confirm
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScanActionSheet;
